import React, { useEffect, useLayoutEffect, useState, useRef, useCallback } from "react";
import { emitAgentSetup } from '../../../api/createAppSocket';
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import Avatar from "@mui/material/Avatar";
import { ReactComponent as Jacob } from "../../../assets/images/ChatImages/albert.svg";
import { ReactComponent as Laptop } from "../../../assets/images/ChatImages/laptop.svg";
import { ListAllRoomsApi } from "../../../store/features/ListAllRooms";
import { getMessageById } from "../../../store/features/getRoomById";
import { msgTime } from "../../../utils/index";
import SearchInput from "../../common/SearchInputField";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSocket } from "../../../SocketContext";
import { resolveCustomerIdFromGetMessagePayload } from "../../../utils/messagePayloadCustomer";
import NoChatAvailable from "./NoChatAvailable";
import ChatListSkeleton from "./ChatListSkeleton";
import { ReactComponent as Desktop } from "../../../assets/images/ChatImages/laptop.svg";
import { ReactComponent as Mobile } from "../../../assets/images/mobile-devices.svg";
import { getLiveChatBasePath } from "../../../utils/liveChatPath";
import ChatListRowV2 from "../ChatV2/ChatListRowV2";
import {
  getUnassignedChatListWarmCache,
  setUnassignedChatListWarmCache,
} from "../../../utils/unassignedChatListWarmCache";
import {
  socketPayloadIsUnassignedChat,
  transformSocketRoomToUnassignedListRow,
} from "../../../utils/unassignedSocketRoom";
import { resolveRoomSourceGroup } from "../../../utils/chatRoomPayload";
import useUnknownRoomListRefresh from "../../../hooks/useUnknownRoomListRefresh";
import {
  getChatListReadReceiptFromMessage,
  getChatListReadReceiptFromRoom,
} from "../../../utils/chatListReadReceipt";

function businessIdsMatch(cachedId, currentId) {
  if (cachedId == null || currentId == null) return false;
  return String(cachedId) === String(currentId);
}

function formatChatStartedTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = `0${date.getMonth() + 1}`.slice(-2);
  const day = `0${date.getDate()}`.slice(-2);
  const hours = date.getHours();
  const minutes = `0${date.getMinutes()}`.slice(-2);
  const amOrPm = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;
  return `${year}-${month}-${day} ${formattedHours}:${minutes} ${amOrPm}`;
}

function readWarmUnassignedListSeed(businessId) {
  const wc = getUnassignedChatListWarmCache();
  const warm =
    Boolean(businessId) &&
    wc.hasFetched &&
    businessIdsMatch(wc.businessId, businessId) &&
    Array.isArray(wc.data);
  if (!warm) {
    return { data: [], isLoading: true };
  }
  return { data: wc.data, isLoading: false };
}

function UnAssignedRooms(props) {
  const {
    setSelectUser,
    setGetRoomId,
    getRoomId,
    selectUser,
    refreshChatList,
    roomClicked,
    setChatSideBar,
    fromSearch = false,
    urlRoomId = "",
    setUnAssignedCount,
    removeRoomId,
    assignedRoomId,
    variant,
  } = props;
  const isV2 = variant === "v2";
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.loginToken);
  const agentToken = useSelector((state) => state.loginTokenCoUser);
  const userData = useSelector((state) => state.loginToken);
  const agentData = useSelector((state) => state.loginTokenCoUser);
  const businessId = userData?.response?.user?.business_id;

  const [activeChat, setActiveChat] = useState(null);
  const [data, setData] = useState(() => readWarmUnassignedListSeed(businessId).data);
  const [isLoading, setIsLoading] = useState(() => readWarmUnassignedListSeed(businessId).isLoading);
  const [rmId, setRmId] = useState(null);
  const [activation, setActivation] = useState();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOnlyRows, setSearchOnlyRows] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appliedUrlRoomRef = useRef(null);
  const socketRef = useRef();
  const rmIdRef = useRef(rmId);
  const selectUserRef = useRef(selectUser);
  selectUserRef.current = selectUser;
  const prevRefreshChatListRef = useRef(refreshChatList);
  const getRoomListRef = useRef(null);
  const dataRef = useRef(data);
  dataRef.current = data;
  /** Rooms closed in this session must not reappear if a refetch still lists them. */
  const closedRoomIdsRef = useRef(new Set());
  const scheduleListRefresh = useUnknownRoomListRefresh({
    refetch: () => getRoomListRef.current?.({ background: true }),
    shouldSkip: (roomId) => closedRoomIdsRef.current.has(roomId),
  });

  const chracterPosition = (alphabet) => {
    const alphabets = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const positionOfAlphabet = alphabets.indexOf(alphabet) + 1;
    return positionOfAlphabet;
  };

  const colorCodesForAvatar = [
    "#405261",
    "#FF4D4F",
    "#FA541C",
    "#FA8C16",
    "#FAAD14",
    "#EB2F96",
    "#FADB14",
    "#A0D911",
    "#52C41A",
    "#5CDBD3",
    "#9254DE",
    "#F759AB",
    "#2F54EB",
    "#722ED1",
    "#1890FF",
    "#13C2C2",
    "#2DB7F5",
    "#19BE6B",
    "#30A745",
    "#FA8C16",
    "#FAAD14",
    "#9254DE",
    "#F759AB",
    "#13C2C2",
    "#722ED1",
    "#2DB7F5",
  ];

  const socket = useSocket();

  const getRoomList = useCallback(async (options = {}) => {
    const { background = false } = options;
    if (!background) {
      setIsLoading(true);
    }
    const bid = userData?.response?.user?.business_id;
    try {
      const result = await dispatch(
        ListAllRoomsApi({
          mainUserId: userData?.response?.user?._id,
          business_id: bid,
          page: 1,
          limit: 100,
          roomType: 'unassigned',
        })
      );

      if (!ListAllRoomsApi.fulfilled.match(result)) {
        setIsLoading(false);
        return;
      }

      const payload = result.payload || {};
      // API may omit `rooms`, nest them, or use null when there are zero rooms.
      const roomsArray = Array.isArray(payload.rooms)
        ? payload.rooms
        : Array.isArray(payload?.data?.rooms)
          ? payload.data.rooms
          : [];

      const explicitFailure =
        payload.success === false || payload.success === "false" || payload.success === "False";
      if (explicitFailure) {
        setIsLoading(false);
        return;
      }

      const successLike =
        payload.success === true ||
        payload.success === "true" ||
        payload.success === 1 ||
        payload.Success === true ||
        payload.success == null;

      if (successLike) {
        const loggedInAgentId = userData?.response?.user?._id;
        const outputarray = roomsArray
          .filter(
            (item) =>
              !item?.is_archived && !closedRoomIdsRef.current.has(String(item?._id))
          )
          .map((item) => {
            const users = Array.isArray(item?.users) ? item.users : [];
            // List payloads mix populated users and bare ids — same as socket rows.
            const customerUser =
              users.find(
                (u) =>
                  typeof u === "object" &&
                  u?._id &&
                  u?._id !== loggedInAgentId &&
                  u?.is_guest
              ) ||
              users.find(
                (u) =>
                  typeof u === "object" &&
                  u?._id &&
                  u?._id !== loggedInAgentId
              ) ||
              null;
            const roomUsers = users
              .map((u) =>
                typeof u === "object" && u?._id
                  ? u._id
                  : typeof u === "string" || typeof u === "number"
                    ? String(u)
                    : null
              )
              .filter(Boolean);
            const customerId =
              customerUser?._id ||
              roomUsers.find((id) => String(id) !== String(loggedInAgentId)) ||
              (roomUsers.length === 1 ? roomUsers[0] : null) ||
              null;

            return {
              id: item._id,
              title: item?.roomName,
              description: item?.latestMessageSnapshot?.content ?? item?.latestMessage?.content,
              attachment: item?.latestMessageSnapshot?.file_image || item?.latestMessage?.file_image,
              time: msgTime(item?.latestMessageSnapshot?.createdAt || item?.latestMessage?.createdAt),
              startTime: formatChatStartedTime(item?.createdAt),
              deviceType: customerUser?.deviceType,
              os: customerUser?.os,
              is_unAssigned: true,
              is_archived: false,
              end_session: item?.end_session,
              unAssignedCount: item?.unAssignedMessageCount || 0,
              iso: item?.latestMessageSnapshot?.createdAt || item?.latestMessage?.createdAt,
              isLive: customerUser ? !!customerUser?.socket_active : false,
              customerId,
              roomUsers,
              businessId: item?.business_id,
              assignedTo: item?.assigned_to,
              tags: item?.tags || [],
              is_guest: customerUser?.is_guest || false,
              sourceGroup: resolveRoomSourceGroup(item),
              readReceipt: getChatListReadReceiptFromRoom(
                item,
                item?.unAssignedMessageCount || 0
              ),
            };
          });

        setData(outputarray);
        const paginationTotal =
          payload.pagination?.total ?? result.payload?.pagination?.total;
        const droppedClosedRooms = roomsArray.length - outputarray.length;
        const listTotal =
          typeof paginationTotal === "number"
            ? Math.max(0, paginationTotal - droppedClosedRooms)
            : outputarray.length;
        if (bid) {
          setUnassignedChatListWarmCache({
            businessId: bid,
            data: outputarray,
            hasFetched: true,
            total: listTotal,
          });
        }
        if (setUnAssignedCount) {
          setUnAssignedCount(listTotal);
        }

        if (socket && roomsArray.length) {
          roomsArray.forEach((room) => {
            socket.emit("join chat", room._id);
          });
        }
      }
    } catch (error) {
      toast.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [userData?.response?.user?._id, userData?.response?.user?.business_id, socket, dispatch, setUnAssignedCount]);

  getRoomListRef.current = getRoomList;

  // If login token / business id hydrate after first paint, apply warm cache before paint (no skeleton flash).
  useLayoutEffect(() => {
    if (!businessId) return;
    const wc = getUnassignedChatListWarmCache();
    if (
      !wc.hasFetched ||
      !businessIdsMatch(wc.businessId, businessId) ||
      !Array.isArray(wc.data)
    ) {
      return;
    }
    setData(wc.data);
    setIsLoading(false);
  }, [businessId]);

  useEffect(() => {
    if (!removeRoomId) return;
    closedRoomIdsRef.current.add(String(removeRoomId));
    setData((prev) => {
      if (!prev.some((room) => room.id === removeRoomId)) return prev;
      return prev.filter((room) => room.id !== removeRoomId);
    });
  }, [removeRoomId]);

  useEffect(() => {
    if (!assignedRoomId) return;
    const id = String(assignedRoomId);
    closedRoomIdsRef.current.add(id);
    setData((prev) => {
      if (!prev.some((room) => String(room.id) === id)) return prev;
      return prev.filter((room) => String(room.id) !== id);
    });
  }, [assignedRoomId]);

  useEffect(() => {
    const toggled = prevRefreshChatListRef.current !== refreshChatList;
    prevRefreshChatListRef.current = refreshChatList;
    const wc = getUnassignedChatListWarmCache();
    const warm =
      Boolean(businessId) &&
      wc.hasFetched &&
      businessIdsMatch(wc.businessId, businessId) &&
      Array.isArray(wc.data);

    const run = getRoomListRef.current;
    if (!run) return;

    if (!toggled && warm) {
      void run({ background: true });
      return;
    }
    void run({ background: false });
    // Intentionally omit getRoomList from deps: identity changes would retrigger a foreground fetch and skeleton.
  }, [refreshChatList, businessId]);

  useEffect(() => {
    if (!fromSearch) setSearchOnlyRows(null);
  }, [fromSearch]);

  // Open room from URL (roomId) on load when viewing unassigned tab
  useEffect(() => {
    const roomIdFromUrl = searchParams.get("roomId");
    if (!roomIdFromUrl) return;

    if (!fromSearch) {
      if (!data?.length) return;
      if (appliedUrlRoomRef.current === roomIdFromUrl) return;
      const roomToSelect = data.find((item) => item.id === roomIdFromUrl);
      if (!roomToSelect) return;
      appliedUrlRoomRef.current = roomIdFromUrl;
      setChatSideBar(true);
      handleUserClick(roomToSelect);
      handleChatClick(roomToSelect.id);
      roomClicked(roomToSelect.id);
      RoomByIdHandler(roomToSelect.id);
      return;
    }

    if (appliedUrlRoomRef.current === roomIdFromUrl) return;
    appliedUrlRoomRef.current = roomIdFromUrl;
    setChatSideBar(true);
    const roomToSelect = data?.find((item) => item.id === roomIdFromUrl);
    if (roomToSelect) {
      handleUserClick(roomToSelect);
      handleChatClick(roomToSelect.id);
      roomClicked(roomToSelect.id);
      RoomByIdHandler(roomToSelect.id);
    } else {
      setSelectUser({
        id: roomIdFromUrl,
        title: "Loading...",
        time: "",
        is_unAssigned: true,
        is_archived: false,
        customerId: null,
        roomUsers: [],
        businessId: userData?.response?.user?.business_id,
      });
      handleChatClick(roomIdFromUrl);
      roomClicked(roomIdFromUrl);
      RoomByIdHandler(roomIdFromUrl);
    }
  }, [searchParams, data, fromSearch]);

  // Chat V2: when landing with no selection (and no deep-link roomId), open the top chat.
  useEffect(() => {
    if (!isV2 || fromSearch || isLoading) return;
    // Only while Unassigned is the active tab — avoid racing other tabs during switch.
    if ((searchParams.get("tab") || "").toLowerCase() !== "unassigned") return;
    if (searchParams.get("roomId")) return;
    if (selectUserRef.current) return;
    if (!data?.length) return;

    const top = data[0];
    if (!top?.id) return;

    navigate(
      `${getLiveChatBasePath(window.location.pathname)}?roomId=${top.id}&tab=unassigned`,
      { replace: true }
    );
  }, [isV2, fromSearch, isLoading, searchParams, data, navigate]);

  useEffect(() => {
    rmIdRef.current = rmId;
  }, [rmId]);

  const handleEndRooms = useCallback((feedbackData) => {
    const roomId =
      feedbackData?.roomId ?? feedbackData?.room_id ?? feedbackData?._id;
    if (!roomId) return;

    // An ended chat belongs to the Closed tab, so it leaves Unassigned.
    closedRoomIdsRef.current.add(String(roomId));
    const wasListed = (dataRef.current || []).some(
      (room) => String(room.id) === String(roomId)
    );
    if (wasListed) {
      setData((prevData) =>
        prevData.filter((room) => String(room.id) !== String(roomId))
      );
    }

    setSelectUser((prev) => {
      if (!prev || String(prev.id) !== String(roomId)) return prev;
      return {
        ...prev,
        end_session: true,
        endSession: true,
        endSessionTime: prev.endSessionTime || new Date().toISOString(),
        isOnline: false,
        isLive: false,
      };
    });

    if (wasListed) {
      // Resyncs the Unassigned badge total with the server.
      void getRoomList({ background: true });
    }
  }, [getRoomList, setSelectUser]);

  const handleArchiveChat = useCallback((payload) => {
    const roomId =
      typeof payload === "string" || typeof payload === "number"
        ? payload
        : payload?.roomId ?? payload?.room_id ?? payload?._id ?? payload?.id;
    if (roomId == null || roomId === "") return;

    // A closed chat lives in the Closed tab, so drop it from Unassigned.
    closedRoomIdsRef.current.add(String(roomId));
    const wasListed = (dataRef.current || []).some(
      (room) => String(room.id) === String(roomId)
    );
    if (wasListed) {
      setData((prevData) =>
        prevData.filter((room) => String(room.id) !== String(roomId))
      );
    }

    setSelectUser((prev) => {
      if (!prev || String(prev.id) !== String(roomId)) return prev;
      return {
        ...prev,
        end_session: true,
        endSession: true,
        endSessionTime: prev.endSessionTime || new Date().toISOString(),
        is_archived: true,
        isOnline: false,
        isLive: false,
      };
    });

    if (wasListed) {
      // Resyncs the Unassigned badge total with the server.
      void getRoomList({ background: true });
    }
  }, [getRoomList, setSelectUser]);

  const handleCustomerStatus = useCallback(({ userId, isActive, userType }) => {
    if (userType !== "customer" || userId == null) return;
    const statusUserId = String(userId);
    const nextLive = !!isActive;
    setData((prevData) => {
      let changed = false;
      const next = prevData.map((room) => {
        const matches =
          String(room.customerId) === statusUserId ||
          (Array.isArray(room.roomUsers) &&
            room.roomUsers.some((id) => String(id) === statusUserId));
        if (!matches) return room;
        if (room.isLive === nextLive) return room;
        changed = true;
        return {
          ...room,
          isLive: nextLive,
          isOnline: isActive ? room.isOnline : false,
        };
      });
      return changed ? next : prevData;
    });

    setSelectUser?.((prev) => {
      if (!prev) return prev;
      const matches =
        String(prev.customerId) === statusUserId ||
        (Array.isArray(prev.roomUsers) &&
          prev.roomUsers.some((id) => String(id) === statusUserId));
      if (!matches || !!prev.isLive === nextLive) return prev;
      return {
        ...prev,
        isLive: nextLive,
        isOnline: isActive ? prev.isOnline : false,
      };
    });
  }, [setSelectUser]);

  const handleRoomCreated = useCallback(
    (newRoom) => {
      if (!newRoom?._id) return;
      if (
        businessId != null &&
        newRoom.business_id != null &&
        !businessIdsMatch(newRoom.business_id, businessId)
      ) {
        return;
      }
      if (!socketPayloadIsUnassignedChat(newRoom)) return;

      const loggedInAgentId = userData?.response?.user?._id;
      const row = transformSocketRoomToUnassignedListRow(newRoom, loggedInAgentId);
      if (!row) return;

      // Open the new chat only when nothing is already open (Chat V2).
      const shouldAutoOpen = isV2 && !selectUserRef.current;

      setData((prev) => {
        if (prev.some((r) => r.id === row.id)) return prev;
        return [row, ...prev];
      });

      const wc = getUnassignedChatListWarmCache();
      if (
        wc.hasFetched &&
        businessIdsMatch(wc.businessId, businessId) &&
        Array.isArray(wc.data) &&
        !wc.data.some((r) => r.id === row.id)
      ) {
        const nextTotal =
          typeof wc.total === "number" ? wc.total + 1 : wc.data.length + 1;
        setUnassignedChatListWarmCache({
          businessId: wc.businessId,
          hasFetched: true,
          data: [row, ...wc.data],
          total: nextTotal,
        });
      }

      if (socket) {
        socket.emit("join chat", row.id);
      }
      void getRoomList({ background: true });

      if (shouldAutoOpen) {
        navigate(
          `${getLiveChatBasePath(window.location.pathname)}?roomId=${row.id}&tab=unassigned`,
          { replace: true }
        );
      }
    },
    [businessId, userData?.response?.user?._id, getRoomList, socket, isV2, navigate]
  );

  useEffect(() => {
    if (!socket) return;
    emitAgentSetup(socket);
    const onConnected = () => {
      console.log("Socket Connected (UnAssignedRooms)");
    };
    socket.on("connected", onConnected);
    socket.on("room-created-notification", handleRoomCreated);
    socket.on("end-room", handleEndRooms);
    socket.on("end-chat", handleEndRooms);
    socket.on("archive-chat", handleArchiveChat);
    socket.on("update_user_status", handleCustomerStatus);
    return () => {
      socket.off("connected", onConnected);
      socket.off("room-created-notification", handleRoomCreated);
      socket.off("end-room", handleEndRooms);
      socket.off("end-chat", handleEndRooms);
      socket.off("archive-chat", handleArchiveChat);
      socket.off("update_user_status", handleCustomerStatus);
    };
  }, [
    socket,
    handleRoomCreated,
    handleEndRooms,
    handleArchiveChat,
    handleCustomerStatus,
    userData?.response?.user?._id,
  ]);

  const RoomByIdHandler = async (rowId) => {
    if (
      rowId === selectUser?.id &&
      Array.isArray(getRoomId) &&
      getRoomId.length > 0
    ) {
      return;
    }
    setGetRoomId([]);
    const listCoUserList = dispatch(
      getMessageById({
        id: rowId,
      })
    );
    listCoUserList
      .then(async (apiData) => {
        if (typeof apiData.payload === "undefined") {
          toast.error("Some Error Occured. Please try again later.");
        } else if (apiData?.payload?.status === 200) {
          if (socket) socket.emit("join chat", rowId);
          const messages = Array.isArray(apiData?.payload?.messages)
            ? apiData.payload.messages
            : [];
          setGetRoomId(messages);

          const payloadRoomForStart =
            apiData?.payload?.room ||
            apiData?.payload?.chatRoom ||
            apiData?.payload?.data?.room ||
            null;
          const startFromRoom = formatChatStartedTime(payloadRoomForStart?.createdAt);
          if (startFromRoom) {
            setSelectUser((prev) => {
              if (!prev || prev.id !== rowId) return prev;
              return { ...prev, startTime: prev.startTime || startFromRoom };
            });
          }

          const fsOpen =
            searchParams.get("fromSearch") === "1" ||
            searchParams.get("fromSearch") === "true";
          const ridOpen = searchParams.get("roomId");
          const roomInProps =
            Array.isArray(data) && data.some((r) => r.id === rowId);
          if (fsOpen && ridOpen === rowId && !roomInProps) {
            const payloadRoom =
              apiData?.payload?.room ||
              apiData?.payload?.chatRoom ||
              apiData?.payload?.data?.room ||
              null;
            const payloadUsers = Array.isArray(payloadRoom?.users)
              ? payloadRoom.users
              : [];
            const loggedInAgentId = userData?.response?.user?._id;
            const visitorUser =
              payloadUsers.find(
                (user) => user?._id && user?._id !== loggedInAgentId
              ) ||
              payloadUsers[0] ||
              null;
            const latestMessage = messages[messages.length - 1];
            const visitorMessage =
              [...messages].reverse().find((m) => !m?.isAgent) || null;
            const customerIdResolved = resolveCustomerIdFromGetMessagePayload(
              apiData.payload,
              {
                loggedInAgentId,
                payloadUsers,
                visitorMessage,
              }
            );
            const fallbackTitle =
              payloadRoom?.roomName ||
              apiData.payload?.roomName ||
              visitorMessage?.sender_name ||
              latestMessage?.sender_name ||
              "Visitor";
            const latestTime =
              latestMessage?.createdAt ||
              payloadRoom?.updatedAt ||
              payloadRoom?.createdAt ||
              "";

            setSelectUser((prev) => ({
              ...(prev || {}),
              id: rowId,
              title:
                prev?.title && prev.title !== "Loading..."
                  ? prev.title
                  : fallbackTitle,
              time: latestTime ? msgTime(latestTime) : prev?.time || "",
              startTime:
                formatChatStartedTime(payloadRoom?.createdAt || latestTime) ||
                prev?.startTime ||
                "",
              customerId: customerIdResolved || prev?.customerId || null,
              roomUsers:
                payloadUsers.map((u) => u?._id).filter(Boolean).length > 0
                  ? payloadUsers.map((u) => u?._id).filter(Boolean)
                  : customerIdResolved
                  ? [customerIdResolved]
                  : prev?.roomUsers || [],
              businessId:
                payloadRoom?.business_id ||
                userData?.response?.user?.business_id ||
                prev?.businessId,
              is_unAssigned: true,
              is_archived: false,
            }));

            setSearchOnlyRows([
              {
                id: rowId,
                name: userData?.response?.user?.full_name,
                title: fallbackTitle,
                logo: <Jacob />,
                description:
                  latestMessage?.content || visitorMessage?.content || "",
                attachment:
                  latestMessage?.file_image || visitorMessage?.file_image,
                time: latestTime ? msgTime(latestTime) : " ",
                startTime: formatChatStartedTime(payloadRoom?.createdAt || latestTime) || "",
                text: "Windows",
                deviceType: visitorUser?.deviceType,
                os: visitorUser?.os,
                McLogo: <Laptop />,
                is_unAssigned: true,
                is_archived: false,
                end_session: payloadRoom?.end_session,
                num: 0,
                unAssignedCount: payloadRoom?.unAssignedMessageCount || 0,
                iso: latestTime,
                isLive:
                  payloadUsers?.length > 0
                    ? payloadUsers[0]?.socket_active
                    : false,
                customerId: customerIdResolved,
                roomUsers: payloadUsers.map((u) => u?._id).filter(Boolean),
                businessId: payloadRoom?.business_id,
                assignedTo: payloadRoom?.assigned_to,
                tags: payloadRoom?.tags || [],
                is_guest: visitorUser?.is_guest || false,
              },
            ]);
          }
        } else if (apiData?.payload?.status === 500) {
          toast.error("Some Error Occured. Please try again later.");
        } else if (apiData?.payload?.status === 401) {
          toast.error("Login Expired. Please login  and try again.");
          navigate("/login");
        }
      })
      .catch(async (error) => {
        toast.error(error);
      });
  };

  const handleReceivedMessage = useCallback(
    (newMessageReceived) => {
      console.log("handle Received Message called", newMessageReceived);

      // if(soundOn && !newMessageReceived?.message?.isAgent) {
      //   notificationSound.play().catch((error) => {
      //     console.error("Error playing sound:", error);
      //   });
      // }

      const roomId = newMessageReceived?.message.roomId;
      const newMessageContent = newMessageReceived?.message.content;
      const newFile = newMessageReceived?.message.file_image;
      const isAgent = newMessageReceived?.message.isAgent;
      const latestMessageTime = msgTime(newMessageReceived?.message.createdAt);
      const nextReadReceipt = getChatListReadReceiptFromMessage(
        newMessageReceived?.message
      );
      const currentRmId = rmIdRef.current;
      const checkTimestamp = new Date();

      const roomIsListed = (dataRef.current || []).some(
        (room) => String(room?.id) === String(roomId)
      );
      if (!roomIsListed) {
        scheduleListRefresh(roomId);
      }

      setData((prevData) => {
        // Find the room that received the new message
        const updatedData = prevData.map((room) => {
          let isOnline = room.isOnline;
          let timeoutId = room.timeoutId;

          if (room.id === roomId) {
            room.iso = checkTimestamp.toISOString();
            if (!isAgent) {
              isOnline = true; // Set the room online when a non-agent message is received

              // Clear any existing timeout for this room
              if (timeoutId) {
                clearTimeout(room.timeoutId);
              }

              // Set a new timeout to mark the room offline
              timeoutId = setTimeout(() => {
                setData((prevData) => {
                  return prevData.map((r) => {
                    if (r.id === roomId && r.iso === room.iso) {
                      return {
                        ...r,
                        isOnline: false, // Set to offline if no new message received
                      };
                    }
                    return r;
                  });
                });

                // Update localStorage as well
                const roomData =
                  JSON.parse(localStorage.getItem(`room_${roomId}`)) || {};
                if (roomData.iso === room.iso) {
                  roomData.isOnline = false;
                  localStorage.setItem(
                    `room_${roomId}`,
                    JSON.stringify(roomData)
                  );
                }
              }, 90000);
            }
          }

          const roomData = JSON.parse(
            localStorage.getItem(`room_${roomId}`)
          ) || {
            // notification: 0,
            isOnline: false,
          };
          if (currentRmId !== roomId && room.id === roomId && !isAgent) {
            // Increment the notification count
            // const newCount = roomData.notification + 1;
            const currentCount = (parseInt(room.unAssignedCount, 10) || 0) + 1;

            // Update room data
            // roomData.notification = newCount;
            roomData.isOnline = isOnline;
            roomData.iso = room.iso;

            localStorage.setItem(`room_${roomId}`, JSON.stringify(roomData));

            return {
              ...room,
              time: latestMessageTime,
              unAssignedCount: currentCount, // Increment message count
              description: newFile ? "Sent Attachment" : newMessageContent, // Update message content
              attachment: Boolean(newFile),
              isOnline: isOnline,
              timeoutId,
              readReceipt: currentCount > 0 ? null : nextReadReceipt,
            };
          } else if (room.id === roomId) {
            roomData.isOnline = isOnline;
            roomData.iso = room.iso;

            localStorage.setItem(`room_${roomId}`, JSON.stringify(roomData));
            return {
              ...room,
              time: latestMessageTime,
              description: newFile ? "Sent Attachment" : newMessageContent, // Update message content
              attachment: Boolean(newFile),
              isOnline: isOnline,
              timeoutId,
              readReceipt: nextReadReceipt,
            };
          }
          return room;
        });
        // Move the room with the new message to the top
        const roomIndex = updatedData.findIndex((room) => room.id === roomId);
        if (roomIndex !== -1) {
          const [updatedRoom] = updatedData.splice(roomIndex, 1);
          updatedData.unshift(updatedRoom);
        }

        return updatedData;
      });
    },
    [setData, scheduleListRefresh]
  );

  const handleChatClick = (id) => {
    setActiveChat(id);
    setRmId(id);
  };

  const handleUserClick = (user) => {
    // const updatedUser = {
    //   ...user,
    //   is_archived: true
    // };

    setSelectUser(user);
    handleChatClick(user.id);
    roomClicked(user.id);
    setChatSideBar(true)
  };

  const roomIdForOpen = urlRoomId || searchParams.get("roomId") || "";
  const roomInProps = data?.some((r) => r.id === roomIdForOpen);
  const listData =
    fromSearch && searchOnlyRows?.length && !roomInProps
      ? searchOnlyRows
      : data;

  const filteredData = listData.filter((item) =>
    item?.title?.toLowerCase().includes((searchTerm || "").toLowerCase())
  );

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const searchSingleOpen =
    searchParams.get("fromSearch") === "1" ||
    searchParams.get("fromSearch") === "true";
  const showSearchSkeleton =
    searchSingleOpen &&
    searchParams.get("roomId") &&
    (!listData || listData.length === 0);

  if (showSearchSkeleton || isLoading) {
    return <ChatListSkeleton />;
  }

  if (!listData || listData.length === 0) {
    return <NoChatAvailable message="No unassigned chats available yet" />;
  }

  return (
    <>
      {!isV2 && (
        <div className="ChatBar-SearchInput">
          <SearchInput
            placeholder="Search"
            value={searchTerm}
            onChange={handleSearchChange}
            title=""
          />
        </div>
      )}
      <div
        className="ChatUser-Main-Container"
        style={isV2 ? { height: "100%", overflow: "auto", padding: 0 } : undefined}
      >
        {filteredData.map((item) => {
          const activate = () => {
            navigate(
              `${getLiveChatBasePath(window.location.pathname)}?roomId=${item.id}&tab=unassigned`
            );
            handleChatClick(item.id);
            handleUserClick(item);
            RoomByIdHandler(item.id);
          };

          if (isV2) {
            return (
              <ChatListRowV2
                key={item.id}
                item={{
                  ...item,
                  num: item.unAssignedCount || item.num || 0,
                  isLive: item.isLive,
                  isOnline: item.isLive,
                }}
                isActive={activeChat === item.id}
                showTyping={false}
                onClick={activate}
              />
            );
          }

          return (
          <div
            className={`ChatUser-Container ${activeChat === item.id ? "ChatUser-ContainerActive" : ""
              }`}
            key={item.id}
            onClick={activate}
          >
            <p>
              {item.img ? (
                <Avatar
                  src={item.img}
                  alt="users logo"
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    bgcolor:
                      colorCodesForAvatar[
                      chracterPosition(
                        item.title?.substring(0, 1).toUpperCase()
                      )
                      ],
                  }}
                >
                  {item.title?.substring(0, 1).toUpperCase()}
                </Avatar>
              )}
            </p>
            <div className="Chat-User-Main">
              <div className="Chat-User00">
                <div className="Chat-User-Kathryn">
                  {item.status}
                  <span
                    className={`User-Status-Icon ${item.isLive ? "online" : "offline"
                      }`}
                  >
                    {item.isLive ? (
                      <p className="onlineStatus"></p>
                    ) : (
                      <p className="offlineStatus"></p>
                    )}
                  </span>
                  <p className="ChatUser-text"> {item.title}</p>
                </div>
                <p className="ChatUser-time"> {item.time.toLowerCase()}</p>
              </div>
              <div className="Chat-User02">
                <p className="ChatUser-discreption" style={{ color: item?.unAssignedCount !== 0 && "#061522", fontWeight: item?.unAssignedCount !== 0 && "500" }}>
                  {item.attachment ? "Sent Attachment" : item.description || ""}
                </p>
                <p className="ChatUser-time">{item.tickLogo}</p>
                <p className="ChatUser-red">
                  {" "}
                  {item.unAssignedCount !== 0 ? item.unAssignedCount : ""}
                </p>
              </div>
              <div className="Chat-User03">
                {item.deviceType === "desktop" ? <Desktop /> : <Mobile />}
                <p className="ActiveUser-Max">{item?.os}</p>
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </>
  );
}

export default UnAssignedRooms;
